/**
 * Wave 29 Chunk 10 — Data Import index.
 *
 * Pick entity → upload file → Hidro infers columns → review → commit.
 * Lists prior batches with status + rollback affordance.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Upload, FileUp, Loader2, ChevronRight, History, Database, Sparkles, RotateCcw, CheckCircle, AlertTriangle,
  ShieldAlert, Trash2,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

const STATUS_PILLS = {
  parsed: { label: "Uploaded", cls: "bg-zinc-500/20 text-zinc-200" },
  mapped: { label: "Mapped", cls: "bg-sky-500/20 text-sky-200" },
  previewed: { label: "Previewed", cls: "bg-amber-500/20 text-amber-200" },
  committed: { label: "Committed", cls: "bg-emerald-500/20 text-emerald-200" },
  rolled_back: { label: "Rolled back", cls: "bg-rose-500/20 text-rose-200" },
};

export default function DataImportIndex() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [entities, setEntities] = useState([]);
  const [entity, setEntity] = useState("customers");
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Duplicate-scanner state (batch-based — relies on import_batches)
  const [scanEntity, setScanEntity] = useState("inventory");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [selected, setSelected] = useState({}); // batch_id → bool
  const [cleaningUp, setCleaningUp] = useState(false);
  const [dedupingBatchId, setDedupingBatchId] = useState(null);

  // Direct entity-scanner state (works regardless of import_batches)
  const [directEntity, setDirectEntity] = useState("inventory");
  const [directScanning, setDirectScanning] = useState(false);
  const [directScan, setDirectScan] = useState(null);
  const [directDeduping, setDirectDeduping] = useState(false);

  const reloadBatches = async () => {
    try {
      const { data } = await api.get("/imports");
      setBatches(data?.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user || !isHr) return;
    (async () => {
      setBusy(true);
      // Load entities and batches INDEPENDENTLY so a failure on one
      // doesn't blank both. The dropdown was empty on production because
      // /api/imports errored once and Promise.all() rejected, leaving
      // entities at [] even though /api/imports/entities was healthy.
      try {
        const e = await api.get("/imports/entities");
        setEntities(e?.data?.entities || []);
      } catch (err) {
        toast.error("Couldn't load entities: " + (err?.response?.data?.detail || err?.message || "unknown"));
      }
      try {
        const b = await api.get("/imports");
        setBatches(b?.data?.items || []);
      } catch (err) {
        toast.error("Couldn't load import history: " + (err?.response?.data?.detail || err?.message || "unknown"));
      }
      setBusy(false);
    })();
  }, [user, isHr]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/imports?entity=${encodeURIComponent(entity)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Parsed ${data.row_count} row(s) — let's map the columns.`);
      nav(`/tools/import/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const scanForDupes = async () => {
    setScanning(true);
    setScanResult(null);
    setSelected({});
    try {
      const { data } = await api.get(`/imports/scan-duplicates?entity=${encodeURIComponent(scanEntity)}`);
      setScanResult(data);
      // Pre-select every "rollback" recommended batch (cross-batch dupes only — dedupe is a separate per-batch action).
      const pre = {};
      (data.batches || []).forEach((b) => { if (b.recommendation === "rollback") pre[b.id] = true; });
      setSelected(pre);
      const rb = data.summary?.batches_recommended_for_rollback || 0;
      const dd = data.summary?.batches_recommended_for_dedupe || 0;
      if (rb === 0 && dd === 0) {
        toast.success(`No duplicate ${scanEntity} batches found — your data is clean.`);
      } else {
        const parts = [];
        if (dd > 0) parts.push(`${dd} batch(es) need dedupe`);
        if (rb > 0) parts.push(`${rb} batch(es) need rollback`);
        toast.warning(`Found ${parts.join(" + ")}. Total redundant rows: ${(data.summary?.redundant_rows || 0).toLocaleString()}.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed.");
    } finally { setScanning(false); }
  };

  const dedupeBatch = async (bid) => {
    if (!window.confirm("Dedupe this batch? It will hard-delete every duplicate row, keeping ONE row per SKU (the one with the longest name). The other inventory rows from this batch stay untouched.")) return;
    setDedupingBatchId(bid);
    try {
      const { data } = await api.post(`/imports/${bid}/dedupe-rows`);
      toast.success(`Removed ${data.deleted_rows} duplicate row(s) across ${data.deduped_keys} SKU(s).`);
      await scanForDupes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dedupe failed.");
    } finally { setDedupingBatchId(null); }
  };

  const scanDirect = async () => {
    setDirectScanning(true);
    setDirectScan(null);
    try {
      const { data } = await api.get(`/imports/entity-scan?entity=${encodeURIComponent(directEntity)}`);
      setDirectScan(data);
      if ((data.redundant_rows || 0) === 0) {
        toast.success(`No duplicate ${directEntity} found in the live collection.`);
      } else {
        toast.warning(`Live ${directEntity}: ${data.duplicate_keys.toLocaleString()} duplicate keys · ${data.redundant_rows.toLocaleString()} redundant rows.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Direct scan failed.");
    } finally { setDirectScanning(false); }
  };

  const dedupeDirect = async () => {
    const r = directScan?.redundant_rows || 0;
    if (!r) { toast.error("No duplicates to clean."); return; }
    if (!window.confirm(`Hard-delete ${r.toLocaleString()} duplicate row(s) from the live ${directEntity} collection? Keeps ONE row per ${directScan?.primary_key} (the one with the longest name). This operates directly on the entity table, independent of import history. Cannot be undone.`)) return;
    setDirectDeduping(true);
    try {
      const { data } = await api.post(`/imports/entity-dedupe?entity=${encodeURIComponent(directEntity)}`);
      toast.success(`Deleted ${data.deleted_rows.toLocaleString()} row(s) across ${data.deduped_keys.toLocaleString()} ${directScan?.primary_key}(s).`);
      await scanDirect();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dedupe failed.");
    } finally { setDirectDeduping(false); }
  };

  const rollbackSelected = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) {
      toast.error("Pick at least one batch to roll back.");
      return;
    }
    const msg = `Roll back ${ids.length} batch(es)? This will hard-delete every row those batches inserted and revert any updates. The earliest batch with each SKU is preserved.`;
    if (!window.confirm(msg)) return;
    setCleaningUp(true);
    try {
      const { data } = await api.post("/imports/rollback-batches", { batch_ids: ids });
      const acc = data.accepted?.length || 0;
      const skp = data.skipped?.length || 0;
      toast.success(`Rollback queued for ${acc} batch(es).${skp ? ` ${skp} skipped.` : ""} Progress will show below — refresh in 30 s.`);
      // Wait a beat, then re-scan and reload batches list.
      setTimeout(async () => {
        await reloadBatches();
        await scanForDupes();
      }, 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rollback failed.");
    } finally { setCleaningUp(false); }
  };

  if (!isHr) {
    return <div className="hv-card p-6 text-sm text-[#dde3ec]" data-testid="import-forbidden">Only owners / admins can run data imports.</div>;
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="import-index-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Database className="w-6 h-6 text-[#1FB6C1]" /> Data Import
        </h1>
      </div>

      <div className="hv-card p-3 text-xs text-[#cbd5e1] flex items-start gap-2" data-testid="import-hint">
        <Sparkles className="w-4 h-4 text-[#1FB6C1] mt-0.5 shrink-0" />
        <span>
          Upload a CSV or Excel. Hidro infers which column maps to which field, you confirm, dry-run shows a row-by-row diff, then one click commits. Every import is fully reversible.
        </span>
      </div>

      <div className="hv-card p-4 sm:p-5 space-y-3" data-testid="import-upload-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <label className="sm:col-span-1 block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Entity</div>
            <select
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              className="hv-input text-sm w-full"
              data-testid="import-entity-select"
            >
              {entities.length === 0 ? (
                <>
                  <option value="customers">Customers</option>
                  <option value="inventory">Inventory</option>
                </>
              ) : (
                entities.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)
              )}
            </select>
          </label>
          <div className="sm:col-span-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={onPick}
              className="hidden"
              data-testid="import-file-input"
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="hv-btn hv-btn-primary w-full disabled:opacity-50"
              data-testid="import-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Upload spreadsheet"}
            </button>
            <p className="text-[11px] text-[#647087] mt-1">CSV or XLSX, up to 30 MB.</p>
          </div>
        </div>
      </div>

      {/* Duplicate scanner — production cleanup tool */}
      <div className="hv-card p-4 sm:p-5 space-y-3 border border-rose-500/20" data-testid="import-cleanup-card">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-rose-400 shrink-0" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-white">Cleanup duplicate batches</h2>
            <p className="text-[11px] text-[#9aa3b5]">
              If the same file got committed twice (e.g. retries during a CORS outage), this finds the redundant batches and lets you roll them back in one click. The earliest batch with each SKU is always preserved.
            </p>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Entity to scan</div>
            <select
              value={scanEntity}
              onChange={(e) => setScanEntity(e.target.value)}
              className="hv-input text-sm"
              data-testid="cleanup-entity-select"
            >
              {entities.length === 0 ? (
                <>
                  <option value="inventory">Inventory</option>
                  <option value="customers">Customers</option>
                </>
              ) : (
                entities.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)
              )}
            </select>
          </label>
          <button
            type="button"
            onClick={scanForDupes}
            disabled={scanning}
            className="hv-btn hv-btn-ghost text-xs disabled:opacity-50"
            data-testid="cleanup-scan-btn"
          >
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Scan for duplicates
          </button>
        </div>
        {scanResult && (
          <div className="space-y-2" data-testid="cleanup-result">
            <div className="text-[11px] text-[#cbd5e1] flex items-center gap-3 flex-wrap">
              <span><b>{scanResult.summary?.total_committed_batches || 0}</b> committed batches</span>
              <span>·</span>
              <span><b className={(scanResult.summary?.intra_batch_duplicate_rows || 0) ? "text-amber-300" : "text-emerald-300"}>{(scanResult.summary?.intra_batch_duplicate_rows || 0).toLocaleString()}</b> intra-batch dupes</span>
              <span>·</span>
              <span><b className={(scanResult.summary?.cross_batch_duplicate_rows || 0) ? "text-rose-300" : "text-emerald-300"}>{(scanResult.summary?.cross_batch_duplicate_rows || 0).toLocaleString()}</b> cross-batch dupes</span>
              <span>·</span>
              <span><b className={scanResult.summary?.redundant_rows ? "text-rose-300" : "text-emerald-300"}>{(scanResult.summary?.redundant_rows || 0).toLocaleString()}</b> total redundant rows</span>
            </div>
            {(scanResult.batches || []).length === 0 ? (
              <div className="text-[11px] text-[#647087]">No committed batches for this entity yet.</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left text-[#647087]">
                    <th className="py-1 pr-2"> </th>
                    <th className="py-1 pr-2">Filename</th>
                    <th className="py-1 pr-2">Committed</th>
                    <th className="py-1 pr-2 text-right">Rows in DB</th>
                    <th className="py-1 pr-2 text-right">Unique SKUs</th>
                    <th className="py-1 pr-2 text-right">Intra-dupe</th>
                    <th className="py-1 pr-2 text-right">Cross-dupe</th>
                    <th className="py-1">Verdict</th>
                    <th className="py-1">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {scanResult.batches.map((b) => {
                    const verdict = b.recommendation;
                    const pillCls = verdict === "rollback"
                      ? "bg-rose-500/20 text-rose-200"
                      : verdict === "dedupe"
                        ? "bg-amber-500/20 text-amber-200"
                        : "bg-emerald-500/20 text-emerald-200";
                    const verdictLabel = verdict === "rollback" ? "Rollback" : verdict === "dedupe" ? "Dedupe" : "Keep";
                    return (
                      <tr key={b.id} className="border-t border-white/5" data-testid={`cleanup-row-${b.id}`}>
                        <td className="py-1 pr-2">
                          {verdict === "rollback" ? (
                            <input
                              type="checkbox"
                              checked={!!selected[b.id]}
                              onChange={(e) => setSelected((s) => ({ ...s, [b.id]: e.target.checked }))}
                              data-testid={`cleanup-check-${b.id}`}
                            />
                          ) : null}
                        </td>
                        <td className="py-1 pr-2 text-white truncate max-w-[180px]">{b.filename || "(no name)"}</td>
                        <td className="py-1 pr-2 text-[#9aa3b5]">{b.committed_at ? new Date(b.committed_at).toLocaleString() : "—"}</td>
                        <td className="py-1 pr-2 text-right text-[#dde3ec]">{(b.inserted_rows || 0).toLocaleString()}</td>
                        <td className="py-1 pr-2 text-right text-emerald-300">{(b.unique_keys || 0).toLocaleString()}</td>
                        <td className={"py-1 pr-2 text-right " + (b.intra_batch_duplicate_rows ? "text-amber-300 font-bold" : "text-[#647087]")}>{(b.intra_batch_duplicate_rows || 0).toLocaleString()}</td>
                        <td className={"py-1 pr-2 text-right " + (b.cross_batch_duplicate_keys ? "text-rose-300 font-bold" : "text-[#647087]")}>{(b.cross_batch_duplicate_keys || 0).toLocaleString()}</td>
                        <td className="py-1">
                          <span className={"px-1.5 py-0.5 rounded text-[10px] font-bold " + pillCls}>{verdictLabel}</span>
                        </td>
                        <td className="py-1">
                          {verdict === "dedupe" && (
                            <button
                              type="button"
                              onClick={() => dedupeBatch(b.id)}
                              disabled={dedupingBatchId === b.id}
                              className="hv-btn hv-btn-ghost text-[10px] px-2 py-0.5 disabled:opacity-50"
                              data-testid={`cleanup-dedupe-${b.id}`}
                              title="Keep one row per SKU in this batch, hard-delete the rest"
                            >
                              {dedupingBatchId === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                              Dedupe
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {(scanResult.summary?.batches_recommended_for_rollback || 0) > 0 && (
              <button
                type="button"
                onClick={rollbackSelected}
                disabled={cleaningUp}
                className="hv-btn hv-btn-primary text-xs disabled:opacity-50 mt-2"
                data-testid="cleanup-rollback-btn"
              >
                {cleaningUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Roll back selected batches
              </button>
            )}
          </div>
        )}
      </div>

      {/* Direct entity-wide dedupe — works regardless of import_batches.
          Use this when the batch-scanner above can't find duplicates
          (e.g. batch metadata is lost) but the live entity collection
          still has dupes. Operates straight on the entity table by
          primary_match_key. */}
      <div className="hv-card p-4 sm:p-5 space-y-3 border border-amber-500/20" data-testid="direct-cleanup-card">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-white">Direct entity dedupe (works without import history)</h2>
            <p className="text-[11px] text-[#9aa3b5]">
              Scans the LIVE collection directly by primary key (sku / external_ref) — even if there's no import history. Use this when the batch tool above can't find dupes but you can still see them on the entity page.
            </p>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Entity</div>
            <select
              value={directEntity}
              onChange={(e) => setDirectEntity(e.target.value)}
              className="hv-input text-sm"
              data-testid="direct-entity-select"
            >
              {entities.length === 0 ? (
                <>
                  <option value="inventory">Inventory</option>
                  <option value="customers">Customers</option>
                </>
              ) : (
                entities.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)
              )}
            </select>
          </label>
          <button
            type="button"
            onClick={scanDirect}
            disabled={directScanning}
            className="hv-btn hv-btn-ghost text-xs disabled:opacity-50"
            data-testid="direct-scan-btn"
          >
            {directScanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Scan live collection
          </button>
        </div>
        {directScan && (
          <div className="space-y-2" data-testid="direct-scan-result">
            <div className="text-[11px] text-[#cbd5e1] grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Rows with key" value={(directScan.total_rows_with_key || 0).toLocaleString()} />
              <Stat label="Unique keys" value={(directScan.total_unique_keys || 0).toLocaleString()} cls="text-emerald-300" />
              <Stat label="Duplicate keys" value={(directScan.duplicate_keys || 0).toLocaleString()} cls={directScan.duplicate_keys ? "text-rose-300" : "text-emerald-300"} />
              <Stat label="Redundant rows" value={(directScan.redundant_rows || 0).toLocaleString()} cls={directScan.redundant_rows ? "text-rose-300 font-bold" : "text-emerald-300"} />
            </div>
            {(directScan.rows_without_key || 0) > 0 && (
              <div className="text-[11px] text-amber-300">
                Note: <b>{(directScan.rows_without_key || 0).toLocaleString()}</b> row(s) have no {directScan.primary_key} value — dedupe ignores these.
              </div>
            )}
            {(directScan.sample_top_dupes || []).length > 0 && (
              <div className="text-[11px] text-[#9aa3b5]">
                Sample dupes: {directScan.sample_top_dupes.map((d) => `${d.key} (${d.count}×)`).join(" · ")}
              </div>
            )}
            {(directScan.redundant_rows || 0) > 0 && (
              <button
                type="button"
                onClick={dedupeDirect}
                disabled={directDeduping}
                className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
                data-testid="direct-dedupe-btn"
              >
                {directDeduping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Dedupe live collection
              </button>
            )}
          </div>
        )}
      </div>

      <div className="hv-card overflow-hidden" data-testid="import-batches-card">
        <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2">
            <History className="w-4 h-4 text-[#647087]" /> Previous imports
          </h2>
          <span className="text-[10px] text-[#647087]">{batches.length}</span>
        </div>
        {busy && batches.length === 0 ? (
          <div className="p-6 text-[#9aa3b5] text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : batches.length === 0 ? (
          <div className="p-6 text-[#9aa3b5] text-sm text-center">No imports yet — upload your first file above.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {batches.map((b) => {
              const pill = STATUS_PILLS[b.status] || { label: b.status, cls: "bg-white/5 text-[#dde3ec]" };
              return (
                <li key={b.id} className="px-4 py-3" data-testid={`import-batch-${b.id}`}>
                  <Link to={`/tools/import/${b.id}`} className="flex items-center gap-3 group">
                    <FileUp className="w-4 h-4 text-[#647087] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white truncate">{b.filename || "(no name)"}</span>
                        <span className={"px-1.5 py-0.5 rounded text-[10px] font-bold " + pill.cls}>{pill.label}</span>
                        <span className="text-[10px] text-[#647087]">{b.entity}</span>
                      </div>
                      <div className="text-[11px] text-[#9aa3b5] mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{b.row_count} rows</span>
                        {b.totals?.new ? <span className="text-emerald-300">· {b.totals.new} new</span> : null}
                        {b.totals?.update ? <span className="text-sky-300">· {b.totals.update} update</span> : null}
                        {b.totals?.conflict ? <span className="text-amber-300">· {b.totals.conflict} conflict</span> : null}
                        {b.totals?.skip ? <span className="text-zinc-400">· {b.totals.skip} skip</span> : null}
                        <span>· {new Date(b.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#647087] group-hover:text-white shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, cls = "text-[#dde3ec]" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">{label}</div>
      <div className={"text-sm font-bold " + cls}>{value}</div>
    </div>
  );
}

